import * as Clipboard from "expo-clipboard";
import { useTheme } from "expo-router/react-navigation";
import { ComponentRef, FC, useMemo, useRef, useState } from "react";
import { View } from "react-native";

import { BarChart } from "@/components/charts";
import { Box, Card, Center, HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { QuestionResults } from "@/types/resources";
import ChartCopyButton from "./ChartCopyButton";
import { copyChartToClipboard } from "./copyChartToClipboard";

/* ── Types ─────────────────────────────────────────────── */

interface NPSResultsProps {
  compact?: boolean;
  result: QuestionResults;
}

/* ── Helpers ────────────────────────────────────────────── */

const PROMOTER_COLOR = "#22c55e";
const PASSIVE_COLOR = "#f59e0b";
const DETRACTOR_COLOR = "#ef4444";

/* ── Component ──────────────────────────────────────────── */

const NPSResults: FC<NPSResultsProps> = ({ compact = false, result }) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const { groups } = result;

  const scoreRef = useRef<ComponentRef<typeof View>>(null);
  const chartRef = useRef<ComponentRef<typeof View>>(null);
  const [scoreCopied, setScoreCopied] = useState(false);
  const [chartCopied, setChartCopied] = useState(false);

  // NPS uses a fixed 0-10 rating (no shifting): 0-6 detractors, 7-8 passives, 9-10 promoters.
  const { counts, detractors, passives, promoters, total } = useMemo(() => {
    const c = Array.from({ length: 11 }, () => 0);
    let p = 0;
    let pa = 0;
    let d = 0;
    groups.forEach((g) =>
      g.answers.forEach(({ answer }) => {
        if (answer.type !== "nps" || answer.rating === null) return;
        const r = answer.rating;
        if (r >= 0 && r <= 10) c[r]++;
        if (r >= 9) p++;
        else if (r >= 7) pa++;
        else d++;
      })
    );
    return {
      counts: c,
      detractors: d,
      passives: pa,
      promoters: p,
      total: p + pa + d,
    };
  }, [groups]);

  const promoterPct = total > 0 ? (promoters / total) * 100 : 0;
  const passivePct = total > 0 ? (passives / total) * 100 : 0;
  const detractorPct = total > 0 ? (detractors / total) * 100 : 0;

  const npsScore = total > 0 ? Math.round(promoterPct - detractorPct) : null;
  const npsColor =
    npsScore === null
      ? "hsl(220,10%,46%)"
      : npsScore >= 50
        ? PROMOTER_COLOR
        : npsScore >= 0
          ? PASSIVE_COLOR
          : DETRACTOR_COLOR;

  const segments = [
    {
      color: PROMOTER_COLOR,
      count: promoters,
      label: i18n.t("dashboardNavigator.npsPromoters"),
      pct: promoterPct,
      range: "9–10",
    },
    {
      color: PASSIVE_COLOR,
      count: passives,
      label: i18n.t("dashboardNavigator.npsPassives"),
      pct: passivePct,
      range: "7–8",
    },
    {
      color: DETRACTOR_COLOR,
      count: detractors,
      label: i18n.t("dashboardNavigator.npsDetractors"),
      pct: detractorPct,
      range: "0–6",
    },
  ];

  const barChartPoints = useMemo(
    () => counts.map((y, idx) => ({ x: String(idx), y })),
    [counts]
  );

  const copyScore = async () => {
    const element = scoreRef.current as unknown as HTMLElement;
    if (!element) return;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(element, {
        backgroundColor: "#ffffff",
        scale: 2,
      });
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = (reader.result as string).split(",")[1];
          await Clipboard.setImageAsync(base64);
          setScoreCopied(true);
          setTimeout(() => setScoreCopied(false), 2000);
        };
        reader.readAsDataURL(blob);
      }, "image/png");
    } catch (error) {
      console.error("Failed to copy NPS score:", error);
    }
  };

  const copyChart = async () => {
    const element = chartRef.current as unknown as HTMLElement;
    if (!element) return;
    await copyChartToClipboard(element, () => {
      setChartCopied(true);
      setTimeout(() => setChartCopied(false), 2000);
    });
  };

  return (
    <HStack className="gap-3 items-stretch flex-col lg:flex-row">
      {/* Card 1 — NPS score + Promoter/Passive/Detractor breakdown */}
      <Card
        className={`group rounded-2xl w-full min-w-0 lg:flex-1 ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 p-6"}`}
      >
        <ChartCopyButton
          compact={compact}
          copied={scoreCopied}
          onPress={copyScore}
          textColor={theme.colors.text}
        />
        <VStack className="gap-4 flex-1">
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
            {i18n.t("dashboardNavigator.chartTitleNps", { count: total })}
          </Text>
          <View
            ref={scoreRef}
            style={{
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              width: "100%",
            }}
          >
            {npsScore === null ? (
              <Center>
                <Text className="text-typography-500">
                  {i18n.t("dashboardNavigator.noData")}
                </Text>
              </Center>
            ) : (
              <VStack className="gap-5 w-full max-w-md">
                {/* Big NPS score */}
                <VStack className="items-center gap-1">
                  <Text
                    className="font-bold"
                    style={{ color: npsColor, fontSize: 64, lineHeight: 1.1 }}
                  >
                    {npsScore}
                  </Text>
                  <Text className="text-sm text-typography-500">
                    {i18n.t("dashboardNavigator.npsScale")}
                  </Text>
                </VStack>
                {/* Segmented bar */}
                <HStack
                  className="w-full overflow-hidden rounded-lg"
                  style={{ height: 16 }}
                >
                  {segments.map((segment) =>
                    segment.pct > 0 ? (
                      <Box
                        key={segment.label}
                        style={{
                          backgroundColor: segment.color,
                          width: `${segment.pct}%`,
                        }}
                      />
                    ) : null
                  )}
                </HStack>
                {/* Legend rows */}
                <VStack className="gap-2.5">
                  {segments.map((segment) => (
                    <HStack
                      className="items-center justify-between"
                      key={segment.label}
                    >
                      <HStack className="items-center gap-2">
                        <Box
                          style={{
                            backgroundColor: segment.color,
                            borderRadius: 3,
                            height: 12,
                            width: 12,
                          }}
                        />
                        <Text className="text-sm text-typography-900 dark:text-typography-100">
                          {segment.label}
                        </Text>
                        <Text className="text-xs text-typography-500">
                          {segment.range}
                        </Text>
                      </HStack>
                      <Text className="text-sm font-medium text-typography-900 dark:text-typography-100">
                        {Math.round(segment.pct)}% ({segment.count})
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            )}
          </View>
        </VStack>
      </Card>

      {/* Card 2 — Rating distribution (0-10) */}
      <Card
        className={`group rounded-2xl w-full min-w-0 lg:flex-1 ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 px-6 pt-6 pb-2"}`}
      >
        <ChartCopyButton
          compact={compact}
          copied={chartCopied}
          onPress={copyChart}
          textColor={theme.colors.text}
        />
        <VStack className="gap-6 flex-1">
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
            {i18n.t("dashboardNavigator.chartTitleNpsBreakdown", {
              count: total,
            })}
          </Text>
          <View
            ref={chartRef}
            style={{
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              width: "100%",
            }}
          >
            <BarChart points={barChartPoints} totalRespondents={total} />
          </View>
        </VStack>
      </Card>
    </HStack>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default NPSResults;
